import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { getActiveCurrency } from '../Shared/Formatters';

/**
 * AnalyticsTable — sortable, paginated rendering of the analytics rows.
 * Active metrics drive the column set so the user only sees what they asked for.
 */

const COLUMNS = [
    { key: 'groupByValue',           label: 'Group',          alwaysShow: true,  num: false },
    { key: 'sales',                  label: 'Sales',          flag: 'sales',                num: true, money: true },
    { key: 'transactionCount',       label: 'Transactions',   flag: 'transCount',           num: true },
    { key: 'atv',                    label: 'ATV',            flag: 'atv',                  num: true, money: true },
    { key: 'revenueShareAmount',     label: 'Rev Share',      flag: 'revenueShareAmount',   num: true, money: true },
    { key: 'revenueSharePercentage', label: 'Rev Share %',    flag: 'revenueSharePercentage', num: true },
    { key: 'baseRent',               label: 'Base Rent',      flag: 'baseRent',             num: true, money: true },
];

const PAGE_SIZE = 25;

export default function AnalyticsTable({ rows, compareRows, metricsFlags, groupBy, onExportCsv }) {
    const { t } = useTranslation();
    const compareOn = Array.isArray(compareRows) && compareRows.length > 0;

    // Build a lookup so we can find the matching compare row for each row.
    // groupBy=date aligns by index; other groupBys align by groupByValue.
    const compareLookup = useMemo(() => {
        if (!compareOn) return null;
        if (groupBy === 'date') {
            // Caller provides ordered rows; align by index.
            return (i, _row) => compareRows[i] || null;
        }
        const map = new Map(compareRows.map(r => [String(r.groupByValue || ''), r]));
        return (_i, row) => map.get(String(row.groupByValue || '')) || null;
    }, [compareOn, compareRows, groupBy]);

    const cols = useMemo(
        () => COLUMNS.filter(c => c.alwaysShow || (metricsFlags && metricsFlags[c.flag])),
        [metricsFlags]
    );

    const [sortKey, setSortKey] = useState(null);
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(0);

    const sorted = useMemo(() => {
        const data = Array.isArray(rows) ? rows.slice() : [];
        if (!sortKey) return data;
        const dir = sortDir === 'asc' ? 1 : -1;
        data.sort((a, b) => {
            const av = a[sortKey];
            const bv = b[sortKey];
            const an = typeof av === 'number' ? av : Number(av);
            const bn = typeof bv === 'number' ? bv : Number(bv);
            const aIsNum = Number.isFinite(an);
            const bIsNum = Number.isFinite(bn);
            if (aIsNum && bIsNum) return (an - bn) * dir;
            return String(av || '').localeCompare(String(bv || '')) * dir;
        });
        return data;
    }, [rows, sortKey, sortDir]);

    const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount - 1);
    const slice = useMemo(
        () => sorted.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE),
        [sorted, safePage]
    );

    const onSort = (key) => {
        if (sortKey === key) {
            setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
        setPage(0);
    };

    const handleExport = () => {
        if (typeof onExportCsv === 'function') {
            onExportCsv({ rows: sorted, columns: cols });
        } else {
            downloadCsv(buildCsv(sorted, cols, groupBy), `analytics-${groupBy || 'export'}.csv`);
        }
    };

    return (
        <div className="v3-card v3-anlx-table-card">
            <div className="v3-anlx-table-head">
                <h3 className="v3-anlx-table-title">
                    {sorted.length === 1
                        ? t('Detail · grouped by {{groupBy}} · {{count}} row', { groupBy: groupBy || '—', count: sorted.length })
                        : t('Detail · grouped by {{groupBy}} · {{count}} rows', { groupBy: groupBy || '—', count: sorted.length })}
                </h3>
                <button
                    type="button"
                    className="v3-btn v3-btn-ghost"
                    onClick={handleExport}
                    disabled={!sorted.length}
                    title={t('Download as CSV')}
                >
                    <Download size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
                    {t('Export CSV')}
                </button>
            </div>
            <div className="v3-anlx-table-wrap">
                <table className="v3-anlx-table" role="table">
                    <thead>
                        <tr>
                            {cols.map(c => (
                                <th
                                    key={c.key}
                                    className={c.num ? 'num' : ''}
                                    onClick={() => onSort(c.key)}
                                    aria-sort={sortKey === c.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                                >
                                    {t(c.label)}
                                    <span className="sort-arrow">
                                        {sortKey === c.key ? (sortDir === 'asc' ? '▲' : '▼') : '▴▾'}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {!sorted.length ? (
                            <tr><td colSpan={cols.length} className="v3-anlx-empty">{t('No rows')}</td></tr>
                        ) : (
                            slice.map((row, sliceIdx) => {
                                const absoluteIdx = safePage * PAGE_SIZE + sliceIdx;
                                const cmpRow = compareLookup ? compareLookup(absoluteIdx, row) : null;
                                return (
                                    <tr key={`${row.groupByValue}-${sliceIdx}`}>
                                        {cols.map(c => {
                                            if (!c.num) {
                                                return <td key={c.key}>{formatCell(row[c.key], false)}</td>;
                                            }
                                            const cur = Number(row[c.key]) || 0;
                                            const prv = cmpRow ? Number(cmpRow[c.key]) || 0 : null;
                                            return (
                                                <td key={c.key} className="num">
                                                    <div className="v3-anlx-cell-num">
                                                        {formatCell(cur, true, c.money)}
                                                    </div>
                                                    {compareOn && prv != null && (
                                                        <div className="v3-anlx-cell-cmp">
                                                            <span className="v3-anlx-cell-prv">{formatCell(prv, true, c.money)}</span>
                                                            <CmpDelta cur={cur} prv={prv} />
                                                        </div>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            {sorted.length > PAGE_SIZE && (
                <div className="v3-anlx-pager">
                    <div>{t('Page {{page}} of {{total}}', { page: safePage + 1, total: pageCount })}</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={safePage === 0}>{t('‹ Prev')}</button>
                        <button onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={safePage >= pageCount - 1}>{t('Next ›')}</button>
                    </div>
                </div>
            )}
        </div>
    );
}

function formatCell(v, isNum, isMoney) {
    if (v == null || v === '') return isNum ? (isMoney ? `${getActiveCurrency()} 0` : '0') : '—';
    if (isNum) {
        const n = typeof v === 'number' ? v : Number(v);
        if (!Number.isFinite(n)) return String(v);
        const text = n.toLocaleString(undefined, { maximumFractionDigits: 2 });
        return isMoney ? `${getActiveCurrency()} ${text}` : text;
    }
    return String(v);
}

function CmpDelta({ cur, prv }) {
    if (prv === 0 && cur === 0) return <span className="v3-delta neutral">·</span>;
    if (prv === 0) {
        return <span className={`v3-delta ${cur > 0 ? 'up' : 'down'}`}>—</span>;
    }
    const pct = ((cur - prv) / Math.abs(prv)) * 100;
    const dir = pct > 0.05 ? 'up' : pct < -0.05 ? 'down' : 'neutral';
    const sign = dir === 'up' ? '+' : dir === 'down' ? '' : '';
    return <span className={`v3-delta ${dir}`}>{sign}{pct.toFixed(1)}%</span>;
}

function buildCsv(rows, cols, groupBy) {
    const head = cols.map(c => csvCell(c.label)).join(',');
    const body = rows.map(r => cols.map(c => csvCell(r[c.key])).join(',')).join('\n');
    return `${head}\n${body}\n`;
}
function csvCell(v) {
    if (v == null) return '';
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}
function downloadCsv(text, name) {
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
